"use client"

import React, { useState, useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/Button'
import { FiCalendar, FiClock } from 'react-icons/fi'

interface DateTimePickerProps {
  value?: Date | null
  onChange?: (date: Date | null) => void
  placeholder?: string
  format?: string
  className?: string
  disabled?: boolean
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date and time",
  format = "YYYY-MM-DD HH:mm",
  className = "",
  disabled = false,
}) => {
  const [date, setDate] = useState<Date | null>(value || null)
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(
    date ? dayjs(date).format(format) : ""
  )
  const containerRef = useRef<HTMLDivElement>(null)

  // 更新输入值
  useEffect(() => {
    if (value) {
      setDate(value)
      setInputValue(dayjs(value).format(format))
    } else {
      setDate(null)
      setInputValue("")
    }
  }, [value, format])

  // 点击外部关闭弹窗
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 处理日期选择
  const handleSelectDate = (selectedDate: Date) => {
    const newDate = new Date(selectedDate)
    if (date) {
      // 保留当前时间
      newDate.setHours(date.getHours())
      newDate.setMinutes(date.getMinutes())
    }
    setDate(newDate)
    setInputValue(dayjs(newDate).format(format))
    onChange?.(newDate)
  }

  // 处理时间选择
  const handleTimeChange = (hours: number, minutes: number) => {
    if (date) {
      const newDate = new Date(date)
      newDate.setHours(hours)
      newDate.setMinutes(minutes)
      setDate(newDate)
      setInputValue(dayjs(newDate).format(format))
      onChange?.(newDate)
    } else {
      // 如果没有选择日期，使用当前日期
      const newDate = new Date()
      newDate.setHours(hours)
      newDate.setMinutes(minutes)
      setDate(newDate)
      setInputValue(dayjs(newDate).format(format))
      onChange?.(newDate)
    }
  }

  // 简化版日历组件
  const Calendar = () => {
    const today = new Date()
    const [currentMonth, setCurrentMonth] = useState(date || today)
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDay = firstDay.getDay()

    // 生成月份日期数组
    const days = []
    for (let i = 0; i < startingDay; i++) {
      days.push(null) // 填充月份起始前的空白
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i))
    }

    // 上个月
    const prevMonth = () => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
    }

    // 下个月
    const nextMonth = () => {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
    }

    return (
      <div className="calendar bg-white p-3 rounded-md" style={{ minWidth: '280px' }}>
        <div className="flex justify-between items-center mb-3">
          <button onClick={prevMonth} className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded-full hover:bg-gray-100">
            &lt;
          </button>
          <div className="font-semibold">
            {dayjs(currentMonth).format('MMMM YYYY')}
          </div>
          <button onClick={nextMonth} className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded-full hover:bg-gray-100">
            &gt;
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
            <div key={index} className="text-center text-gray-500 text-xs py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => (
            <button
              key={index}
              disabled={!day}
              className={`
                h-8 w-8 flex items-center justify-center rounded-full text-sm
                ${!day ? 'invisible' : 'hover:bg-indigo-100'}
                ${day && dayjs(day).isSame(date, 'day') ? 'bg-indigo-600 text-white' : ''}
                ${day && dayjs(day).isSame(today, 'day') && !dayjs(day).isSame(date, 'day') ? 'border border-indigo-600' : ''}
              `}
              onClick={() => day && handleSelectDate(day)}
            >
              {day?.getDate()}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // 简化版时间选择器
  const TimePicker = () => {
    // 修复小时选择问题，使用固定数组
    const hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
    const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
    
    const currentHour = date?.getHours() || 0
    const currentMinute = date?.getMinutes() || 0
    
    const hourColumnStyle = {
      display: 'flex',
      flexDirection: 'column' as const,
      width: '40px',
    }
    
    const minuteColumnStyle = {
      display: 'flex',
      flexDirection: 'column' as const,
      width: '40px',
    }
    
    const buttonStyle = {
      width: '100%',
      marginBottom: '4px',
    }
    
    return (
      <div className="time-picker p-2 bg-white rounded-md" style={{ width: '90px' }}>
        <div className="flex justify-center items-center mb-1">
          <div className="font-semibold text-sm">Time</div>
        </div>
        <div className="flex justify-center">
          <div className="flex items-start gap-1 py-1">
            <div className="flex flex-col" style={hourColumnStyle}>
              <div className="text-gray-500 text-xs text-center mb-1">Hr</div>
              <div className="h-[210px] overflow-y-auto custom-scrollbar flex flex-col" style={{ scrollbarWidth: 'none' }}>
                {hours.map((hour) => (
                  <button
                    key={hour}
                    style={buttonStyle}
                    className={`
                      py-1 text-center rounded-md text-sm
                      ${hour === currentHour ? 'bg-indigo-600 text-white' : 'hover:bg-indigo-100'}
                    `}
                    onClick={() => handleTimeChange(hour, currentMinute)}
                  >
                    {hour.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col" style={minuteColumnStyle}>
              <div className="text-gray-500 text-xs text-center mb-1">Min</div>
              <div className="h-[210px] overflow-y-auto pr-1 custom-scrollbar flex flex-col" style={{ scrollbarWidth: 'thin' }}>
                {minutes.map((minute) => (
                  <button
                    key={minute}
                    style={buttonStyle}
                    className={`
                      py-1 text-center rounded-md text-sm
                      ${minute === currentMinute ? 'bg-indigo-600 text-white' : 'hover:bg-indigo-100'}
                    `}
                    onClick={() => handleTimeChange(currentHour, minute)}
                  >
                    {minute.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div 
        className="flex items-center border rounded-md p-2 cursor-pointer hover:border-indigo-600"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <input
          type="text"
          className="flex-grow focus:outline-none cursor-pointer"
          placeholder={placeholder}
          value={inputValue}
          readOnly
          disabled={disabled}
        />
        <FiCalendar className="text-gray-500 ml-2" />
      </div>
      
      {isOpen && (
        <div className="absolute mt-1 z-50 bg-white rounded-md shadow-xl" style={{ width: '400px' }}>
          <div className="flex">
            <Calendar />
            <div className="border-l border-gray-200 mx-1"></div>
            <TimePicker />
          </div>
          <div className="border-t border-gray-200"></div>
          <div className="p-2 flex justify-end">
            <Button 
              variant="solid" 
              className="text-sm px-4 py-1" 
              onClick={() => setIsOpen(false)}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateTimePicker
